import React, { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./datepicker-custom.css";
import {
  Calendar as CalendarIcon,
  Clock,
  X,
  Copy,
  AlertCircle,
  Users,
  Plus,
  CalendarCheck,
  Settings,
  CheckCircle,
  Eye,
  EyeOff,
  RefreshCw,
  Check,
} from "lucide-react";
import { useMeeting } from "../contexts/MeetingContext";
// import { v4 as uuidv4 } from 'uuid';
import { useAuth } from "../contexts/AuthContext";
import { usePreference } from "../contexts/PreferenceContext";
import { useNavigate } from "react-router-dom";


interface CreateMeetingProps {
  onClose?: () => void;
  onCreated?: (meeting: any) => void;
  darkMode?: boolean;
}

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const slugifyTitle = (title: string) => {
  return title
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

const CreateMeeting: React.FC<CreateMeetingProps> = ({
  onClose,
  onCreated,
  darkMode = false,
}) => {
  const [participants, setParticipants] = useState("");
  const [participantEmails, setParticipantEmails] = useState<string[]>([]);

  const [participantInput, setParticipantInput] = useState("");
  const [showParticipantSuggestions, setShowParticipantSuggestions] =
    useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<any[]>([]);
  const [date, setDate] = useState<Date | null>(new Date());
  // const [startTime, setStartTime] = useState("10:00 AM");
  // const [endTime, setEndTime] = useState("11:00 AM");
  const [recurring, setRecurring] = useState(false);
  // const [recurringFrequency, setRecurringFrequency] = useState<"daily" | "weekly" | "biweekly" | "monthly" | "yearly">("weekly");
  const [recurringFrequency, setRecurringFrequency] = useState<"daily" | "weekly" | "biweekly" | "monthly" | "yearly">("weekly");
  const [recurringDays, setRecurringDays] = useState<string[]>(["monday"]);
  const [description, setDescription] = useState("");
  const [showDescription, setShowDescription] = useState(false);
  const [showError, setShowError] = useState(false);
  // const [link] = useState("https://meeting-link.com/abc123");
  const [link, setLink] = useState("");
  const { token, searchUsers } = useAuth();
  const [showLoginError, setShowLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"details" | "security">("details");
  const [securitySettings, setSecuritySettings] = useState({
    requirePassword: false,
    password: "",
    waitingRoom: false,
    muteOnEntry: false,
    screenShare: "host-only",
    recording: false,
    chat: "all",
    participantVideo: true,
    participantAudio: true,
    moderators: [] as string[],
  });

  const previewRoomId = async () => {
    if (meetingId) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/meetings/preview-room-id`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await res.json();
      setMeetingId(data.meetingId);

      const baseUrl = process.env.REACT_APP_MEETING_BASE_URL || "https://dev-meeting.vela.business/";

      // AB YAHAN CHANGE HAI:
      const slugified = slugifyTitle(createMeetingForm.title);
      const safeTitle = slugified ? `-${slugified}` : "";

      setLink(`${baseUrl}${data.meetingId}${safeTitle}`);
    } catch (err) {
      console.error("Failed to preview room id", err);
    }
  };

  const navigate = useNavigate();
  const [createMeetingForm, setCreateMeetingForm] = useState({
    title: "",
    description: "",
    participants: [],
  });
  const [showScheduleMeeting, setShowScheduleMeeting] = useState(false);
  const [passwordConfirmed, setPasswordConfirmed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { createMeeting, joinMeeting, getMeetingDetails } = useMeeting();
  const { timezone } = usePreference();
  const resolvedOrganizerTz =
    !timezone || timezone === "automatic"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : timezone;

  // Time input states
  const [startTimeInput, setStartTimeInput] = useState("");
  const [endTimeInput, setEndTimeInput] = useState("");
  const [showStartTimeDropdown, setShowStartTimeDropdown] = useState(false);
  const [showEndTimeDropdown, setShowEndTimeDropdown] = useState(false);
  const startTimeRef = useRef<HTMLDivElement>(null);
  const endTimeRef = useRef<HTMLDivElement>(null);

  // Recurring date states
  const [recurringStartDate, setRecurringStartDate] = useState<Date | null>(new Date());
  const [recurringEndDate, setRecurringEndDate] = useState<Date | null>(null);



  // Function to generate random password
  const generateRandomPassword = () => {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const [meetingId, setMeetingId] = useState<string>("");

  const previewTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const getCurrentTime = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const period = hours >= 12 ? "PM" : "AM";
    const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

    // Round *up* to next 15-minute interval
    // let roundedMinutes = Math.ceil(minutes / 15) * 15;
    let roundedMinutes = Math.ceil(minutes / 30) * 30;

    let newHour = hours;
    if (roundedMinutes === 60) {
      roundedMinutes = 0;
      newHour = (hours + 1) % 24;
    }

    const newPeriod = newHour >= 12 ? "PM" : "AM";
    const displayNewHour = newHour === 0 ? 12 : newHour > 12 ? newHour - 12 : newHour;
    const displayMinute = String(roundedMinutes).padStart(2, "0");

    return `${displayNewHour}:${displayMinute} ${newPeriod}`;
  };



  const isTimeInPast = (timeString: string, selectedDate: Date | null) => {
    if (!selectedDate) return false;

    const now = new Date();
    const selected = new Date(selectedDate);

    // If selected date is in the future, no times are in the past
    if (selected.toDateString() !== now.toDateString()) {
      return false;
    }

    // Parse the time string
    const [time, period] = timeString.split(" ");
    const [hours, minutes] = time.split(":").map(Number);

    // Convert to 24-hour format
    let hour24 = hours;
    if (period === "PM" && hours !== 12) {
      hour24 = hours + 12;
    } else if (period === "AM" && hours === 12) {
      hour24 = 0;
    }

    // Compare with current time
    const timeInMinutes = hour24 * 60 + minutes;
    const nowInMinutes = now.getHours() * 60 + now.getMinutes();

    return timeInMinutes < nowInMinutes;
  };

  const addOneHour = (timeString: string) => {
    const [time, period] = timeString.split(" ");
    const [hours, minutes] = time.split(":").map(Number);

    // Convert to 24-hour format for easier math
    let totalHours = period === "PM" && hours !== 12 ? hours + 12 : hours;
    if (period === "AM" && hours === 12) totalHours = 0;

    // Add 15 minutes
    // let totalMinutes = totalHours * 60 + minutes + 15;
    let totalMinutes = totalHours * 60 + minutes + 30;
    // Normalize if minutes overflow 24h
    totalMinutes = totalMinutes % (24 * 60);

    // Convert back to hours and minutes
    let newHours = Math.floor(totalMinutes / 60);
    const newMinutes = totalMinutes % 60;

    // Convert back to 12-hour format
    const newPeriod = newHours >= 12 ? "PM" : "AM";
    if (newHours === 0) newHours = 12;
    else if (newHours > 12) newHours -= 12;

    return `${newHours}:${newMinutes.toString().padStart(2, "0")} ${newPeriod}`;
  };


  const backendURL = process.env.REACT_APP_BACKEND_URL || "";


  const currentTime = getCurrentTime();
  console.log("currentTime", currentTime);

  const [startTime, setStartTime] = useState(currentTime);
  const [endTime, setEndTime] = useState(addOneHour(currentTime));
  console.log("endTime", addOneHour(currentTime));


  useEffect(() => {
    if (date) {
      const startOptions = getStartTimeOptions();
      const now = new Date();
      const selected = new Date(date);

      // Only reset if date is today AND current start time is in the past
      if (selected.toDateString() === now.toDateString()) {
        const startMinutes = timeToMinutes(startTime);
        const nowInMinutes = now.getHours() * 60 + now.getMinutes();

        if (startMinutes < nowInMinutes) {
          const newCurrentTime = getCurrentTime();
          setStartTime(newCurrentTime);
          setEndTime(addOneHour(newCurrentTime));
        }
      }
    }
  }, [date]);


  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutsideTime = (event: MouseEvent) => {
      if (startTimeRef.current && !startTimeRef.current.contains(event.target as Node)) {
        setShowStartTimeDropdown(false);
      }
      if (endTimeRef.current && !endTimeRef.current.contains(event.target as Node)) {
        setShowEndTimeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutsideTime);
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideTime);
    };
  }, []);

  // Mock user data for autocomplete (will be combined with real users)
  const mockUsers = [
    {
      id: 1,
      name: "John Smith",
      email: "john.smith@company.com",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg",
    },
    {
      id: 2,
      name: "Sarah Johnson",
      email: "sarah.johnson@company.com",
      avatar: "https://randomuser.me/api/portraits/women/44.jpg",
    },
    {
      id: 3,
      name: "Mike Davis",
      email: "mike.davis@company.com",
      avatar: "https://randomuser.me/api/portraits/men/45.jpg",
    },
    {
      id: 4,
      name: "Emily Wilson",
      email: "emily.wilson@company.com",
      avatar: "https://randomuser.me/api/portraits/women/46.jpg",
    },
    {
      id: 5,
      name: "David Brown",
      email: "david.brown@company.com",
      avatar: "https://randomuser.me/api/portraits/men/47.jpg",
    },
    {
      id: 6,
      name: "Lisa Anderson",
      email: "lisa.anderson@company.com",
      avatar: "https://randomuser.me/api/portraits/women/48.jpg",
    },
    {
      id: 7,
      name: "Alex Thompson",
      email: "alex.thompson@company.com",
      avatar: "https://randomuser.me/api/portraits/men/49.jpg",
    },
    {
      id: 8,
      name: "Maria Garcia",
      email: "maria.garcia@company.com",
      avatar: "https://randomuser.me/api/portraits/women/50.jpg",
    },
  ];

  // State for real users from database
  const [realUsers, setRealUsers] = useState<Array<{ id: number, name: string, email: string, avatar: string | null }>>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  // Combined users (mock + real)
  const allUsers = useMemo(() => {
    // Format real users to match the structure
    const formattedRealUsers = realUsers.map(user => ({
      ...user,
      isMockUser: false
    }));

    return [...formattedRealUsers];
  }, [realUsers]);


  const allTimeOptions = Array.from({ length: 48 }, (_, i) => {
    const totalMinutes = i * 30; // 30-minute intervals
    const hour = Math.floor(totalMinutes / 60);
    const minute = totalMinutes % 60;
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const displayMinute = String(minute).padStart(2, "0");
    return `${displayHour}:${displayMinute} ${period}`;
  });

  // Helper function to convert time string to minutes for comparison
  const timeToMinutes = (timeString: string) => {
    const [time, period] = timeString.split(" ");
    const [hours, minutes] = time.split(":").map(Number);

    let hour24 = hours;
    if (period === "PM" && hours !== 12) {
      hour24 = hours + 12;
    } else if (period === "AM" && hours === 12) {
      hour24 = 0;
    }

    return hour24 * 60 + minutes;
  };
  const getStartTimeOptions = () => {
    if (!date) return allTimeOptions;

    const now = new Date();
    const selected = new Date(date);

    // If selected date is today, filter out past times
    if (selected.toDateString() === now.toDateString()) {
      const nowInMinutes = now.getHours() * 60 + now.getMinutes();
      const filteredTimes = allTimeOptions.filter(time => timeToMinutes(time) >= nowInMinutes);
      return filteredTimes.length > 0 ? filteredTimes : [allTimeOptions[allTimeOptions.length - 1]];
    }

    return allTimeOptions;
  };
  // Filter end time options - only show times after start time
  const getEndTimeOptions = () => {
    const startMinutes = timeToMinutes(startTime);
    // Show only times that are at least 15 minutes after start time
    // return allTimeOptions.filter(time => timeToMinutes(time) > startMinutes);
    return allTimeOptions.filter(time => timeToMinutes(time) >= startMinutes + 30);
  };

  // Validate and format time input


  const validateTimeInput = (input: string): string | null => {
    input = input.trim().toUpperCase();

    const timePattern = /^(\d{1,2}):?(\d{2})?\s*(AM|PM)?$/i;
    const match = input.match(timePattern);

    if (!match) return null;

    let hours = parseInt(match[1]);
    let minutes = match[2] ? parseInt(match[2]) : 0;
    let period = match[3] || '';

    if (hours < 1 || hours > 12) return null;
    if (minutes < 0 || minutes > 59) return null;

    if (!period) {
      const now = new Date();
      const currentHour = now.getHours();

      if (hours < 12 && currentHour >= 12) {
        period = 'PM';
      } else {
        period = 'AM';
      }
    }

    return `${hours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const handleStartTimeInputChange = (value: string) => {
    setStartTimeInput(value);
    setShowStartTimeDropdown(true);
  };

  const handleEndTimeInputChange = (value: string) => {
    setEndTimeInput(value);
    setShowEndTimeDropdown(true);
  };


  const handleStartTimeInputBlur = () => {
    setTimeout(() => {
      if (startTimeInput) {
        const validatedTime = validateTimeInput(startTimeInput);
        if (validatedTime) {
          setStartTime(validatedTime);
          setStartTimeInput('');
        } else {
          setStartTimeInput('');
        }
      }
      setShowStartTimeDropdown(false);
    }, 200);
  };


  const handleEndTimeInputBlur = () => {
    setTimeout(() => {
      if (endTimeInput) {
        const validatedTime = validateTimeInput(endTimeInput);
        if (validatedTime) {
          setEndTime(validatedTime);
          setEndTimeInput('');
        } else {
          setEndTimeInput('');
        }
      }
      setShowEndTimeDropdown(false);
    }, 200);
  };

  const getFilteredStartTimes = () => {
    if (!startTimeInput) return getStartTimeOptions();

    const input = startTimeInput.toLowerCase();
    return getStartTimeOptions().filter(time =>
      time.toLowerCase().includes(input)
    );
  };

  const getFilteredEndTimes = () => {
    if (!endTimeInput) return getEndTimeOptions();

    const input = endTimeInput.toLowerCase();
    return getEndTimeOptions().filter(time =>
      time.toLowerCase().includes(input)
    );
  };

  const handleScheduleMeeting = async () => {
    if (!createMeetingForm.title.trim()) {
      setError("Meeting Title is required.");
      setLoading(false);
      return;
    }

    if (!token) {
      setShowLoginError("You must be logged in to create a meeting.");
      return;
    }

    try {
      setLoading(true);

      if (endTime <= startTime) {
        setShowError(true);
        setLoading(false);
        return;
      }
      setShowError(false);

      const recurringMap: {
        [key: string]: "daily" | "weekly" | "bi-weekly" | "monthly" | "yearly";
      } = {
        daily: "daily",
        weekly: "weekly",
        biweekly: "bi-weekly",
        monthly: "monthly",
        yearly: "yearly",
      };

      const repeatStatusValue = recurring
        ? recurringMap[recurringFrequency]
        : undefined;

      const participantDetails = participantEmails.map((email) => {
        const userInfo = allUsers.find((user) => user.email === email);
        return {
          email,
          name: userInfo ? userInfo.name : email,
          is_moderator: securitySettings.moderators.includes(email),
        };
      });

      const meetingData = {
        title: createMeetingForm.title,
        description: createMeetingForm.description,
        meetingType: "scheduled" as const,
        meetingDate: date || undefined,
        startTime,
        endTime,
        passwordProtection: securitySettings.requirePassword,
        meetingPassword: securitySettings.password,
        waitingRoom: securitySettings.waitingRoom,
        muteOnEntry: securitySettings.muteOnEntry,
        allowRecording: securitySettings.recording,
        moderators: securitySettings.moderators || [],
        participants: participantEmails,
        participantDetails,
        isRecurring: recurring,
        repeatStatus: repeatStatusValue,
        repeatDays:
          recurring && recurringFrequency !== "daily" ? recurringDays : [],
        recurringStartDate: recurring
          ? recurringStartDate ?? undefined
          : undefined,
        recurringEndDate: recurring
          ? recurringEndDate ?? undefined
          : undefined,
        meeting_link: link,
        meetingId: meetingId,
        organizerTimezone: resolvedOrganizerTz,
      };


      const res: any = await createMeeting(meetingData);

      if (res?.meeting?.meeting_link) {
        setLink(res.meeting.meeting_link);
      }
      setLoading(false);

      // Reset form
      setShowScheduleMeeting(false);
      setCreateMeetingForm({ title: "", description: "", participants: [] });
      setDate(new Date());
      const newCurrentTime = getCurrentTime();
      setStartTime(newCurrentTime);
      setEndTime(addOneHour(newCurrentTime));
      setRecurring(false);
      setParticipantEmails([]);

      if (onClose) onClose();
    } catch (err) {
      console.error("Failed to schedule meeting:", err);
      setError("Failed to create meeting. Please try again.");
      setTimeout(() => setError(""), 3000);
      setLoading(false);
    }
  };

  const [copied, setCopied] = useState(false);


  const CopyButton = ({ text, darkMode }: { text: string; darkMode: boolean }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy text:", err);
      }
    };

    return (
      <button
        type="button"
        onClick={handleCopy}
        className={` mt-7 -translate-y-1/2 px-2 py-1 h-8 text-xs rounded-md font-medium transition-all duration-200 ${copied
          ? "bg-green-500 text-white"
          : "text-white bg-gradient-to-r from-pink-500 to-red-500 hover:opacity-90"
          }`}
      >
        {copied ? "Copied" : <Copy size={14} />}
      </button>
    );
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // reset after 2 seconds
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleDayToggle = (day: string) => {
    setRecurringDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };


  const handleParticipantInputChange = async (value: string) => {
    setParticipantInput(value);

    if (value.trim()) {
      // If we have a token, search the database
      if (token && value.length >= 2) {
        setIsLoadingUsers(true);
        try {
          const searchResults = await searchUsers(value);
          // Filter out users that are already in participantEmails
          const filteredResults = searchResults.filter(
            user => !participantEmails.includes(user.email)
          );

          // Update realUsers with the search results
          setRealUsers(searchResults);

          // Set filtered suggestions directly from API results
          setFilteredSuggestions(filteredResults);
          setIsLoadingUsers(false);
        } catch (error) {
          console.error("Error searching users:", error);
          setIsLoadingUsers(false);
          setFilteredSuggestions([]);
        }
      } else {
        // For queries less than 2 characters, filter locally
        const filteredLocal = allUsers.filter(
          (user) =>
            !participantEmails.includes(user.email) &&
            (user.name.toLowerCase().includes(value.toLowerCase()) ||
              user.email.toLowerCase().includes(value.toLowerCase()))
        );
        setFilteredSuggestions(filteredLocal);
      }

      setShowParticipantSuggestions(true);
    } else {
      setShowParticipantSuggestions(false);
      setFilteredSuggestions([]);
      // Reset realUsers when input is cleared
      setRealUsers([]);
    }
  };
  const [duplicateMsg, setDuplicateMsg] = useState<string | null>(null);

  const handleAddParticipant = (email: string, name?: string) => {
    const normalized = email.trim().toLowerCase();
    const alreadyAdded = participantEmails.some((e) => e.toLowerCase() === normalized);
    if (alreadyAdded) {
      setDuplicateMsg(`${normalized} is already in the list`);
      setTimeout(() => setDuplicateMsg(null), 3000);
      setParticipantInput("");
      setShowParticipantSuggestions(false);
      setFilteredSuggestions([]);
      return;
    }
    setParticipantEmails((prev) => [...prev, normalized]);
    setParticipants((prev) => (prev ? `${prev}, ${normalized}` : normalized));

    if (securitySettings.moderators.some((m) => m.toLowerCase() === normalized)) {
      setSecuritySettings(prev => ({
        ...prev,
        moderators: [...prev.moderators, normalized]
      }));
    }
    setDuplicateMsg(null);
    setParticipantInput("");
    setShowParticipantSuggestions(false);
    setFilteredSuggestions([]);
  };

  const handleRemoveParticipant = (email: string) => {
    setParticipantEmails((prev) => prev.filter((e) => e !== email));
    setParticipants((prev) =>
      prev
        .split(", ")
        .filter((e) => e !== email)
        .join(", ")
    );

    // Also remove from moderators if present
    setSecuritySettings(prev => ({
      ...prev,
      moderators: prev.moderators.filter(m => m !== email)
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && participantInput.trim()) {
      e.preventDefault();
      const trimmed = participantInput.trim();
      const exactMatch = filteredSuggestions.find(
        (s) => s.email.toLowerCase() === trimmed.toLowerCase()
      );

      // Typed a full external email that isn't an exact vela-user match -> add it as-is
      if (isValidEmail(trimmed) && !exactMatch) {
        handleAddParticipant(trimmed);
      } else if (filteredSuggestions.length > 0) {
        handleAddParticipant(filteredSuggestions[0].email, filteredSuggestions[0].name);
      }
    }
  };

  const handleParticipantInputFocus = () => {
    if (!participantInput.trim()) {
      // Show a mix of real and mock users, prioritizing real users
      const availableUsers = allUsers.filter(
        (user) => !participantEmails.includes(user.email)
      );
      setFilteredSuggestions(availableUsers.slice(0, 5)); // Show first 5 available users
      setShowParticipantSuggestions(true);
    }
  };

  const handleClickOutside = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    // Close participant suggestions if clicking outside the input container
    if (!target.closest(".participant-input-container")) {
      setShowParticipantSuggestions(false);
    }

    // Close modal if clicking on the backdrop (the outer div)
    if (target === e.currentTarget) {
      onClose?.();
    }
  };

  const handleModeratorToggle = (email: string) => {
    setSecuritySettings((prev) => ({
      ...prev,
      moderators: prev.moderators.includes(email)
        ? prev.moderators.filter((m) => m !== email)
        : [...prev.moderators, email],
    }));
  };

  const handlePasswordConfirm = () => {
    if (securitySettings.password.trim()) {
      setPasswordConfirmed(true);
      // Reset confirmation after 3 seconds
      setTimeout(() => setPasswordConfirmed(false), 3000);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm overflow-hidden"
      onClick={handleClickOutside}
    >
      <div
        className={`rounded-3xl shadow-2xl border w-[95vw] max-w-3xl relative max-h-[100vh] overflow-hidden ${darkMode
          ? "bg-gradient-to-br from-[#1a1a2e] to-[#16213e] border-white/10"
          : "bg-gradient-to-br from-white to-gray-50 border-gray-200"
          }`}
      >
        {/* Close Button */}
        <button
          className={`absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 backdrop-blur-sm ${darkMode
            ? "bg-white/10 hover:bg-white/20 text-white/60 hover:text-white"
            : "bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800"
            }`}
          aria-label="Close"
          onClick={onClose}
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="relative px-8 pt-8 pb-6">
          <div className="text-center flex">
            <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-2xl bg-gradient-to-br from-green-600 to-teal-500 shadow-lg">
              <CalendarIcon size={24} className="text-white" />
            </div>
            <div className="flex-1 flex-col ml-5 text-left gap-5">
              <h2
                className={`text-xl font-bold mb-1 ${darkMode ? "text-white" : "text-gray-900"
                  }`}
              >
                Schedule Meeting
              </h2>
              <p
                className={`text-sm ${darkMode ? "text-white/60" : "text-gray-600"
                  }`}
              >
                Create a new meeting with participants
              </p>
            </div>
            {showLoginError && (
              <div className={`p-4 border rounded-xl mt-2 ${darkMode ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <AlertCircle size={20} className="text-red-500" />
                  </div>
                  <div className="ml-2 text-sm text-red-500">{showLoginError}</div>
                  {/* <div className="flex ml-4" > */}
                  <button
                    className="ml-auto text-sm text-white hover:text-white font-medium border border-white/20 hover:border-white rounded-full px-3 py-1 transition-all duration-200 bg-green-500 hover:bg-green-600"
                    onClick={() => navigate('/login')}
                  >
                    Login
                  </button>
                  {/* </div> */}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div
          className={`flex gap-1 mx-8 mb-4 rounded-xl p-1 ${darkMode ? "bg-white/5" : "bg-gray-100"
            }`}
        >
          <button
            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${activeTab === "details"
              ? "bg-gradient-to-r from-pink-500 to-red-500 text-white shadow-lg"
              : darkMode
                ? "text-white/60 hover:text-white hover:bg-white/5"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
              }`}
            onClick={() => setActiveTab("details")}
          >
            Details
          </button>
          <button
            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${activeTab === "security"
              ? "bg-gradient-to-r from-pink-500 to-red-500 text-white shadow-lg"
              : darkMode
                ? "text-white/60 hover:text-white hover:bg-white/5"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
              }`}
            onClick={() => setActiveTab("security")}
          >
            Security
          </button>
        </div>

        {/* Content Area */}
        <div className="px-8 pb-6 overflow-y-auto max-h-[45vh] scrollbar-hide">
          {activeTab === "details" && (
            <div className="space-y-5">
              {/* Meeting Name */}
              {error && (
                <div
                  className={`mx-5 mb-6 p-3 rounded-xl text-center text-sm ${darkMode
                    ? "bg-red-600/20 border border-red-500/30 text-red-400"
                    : "bg-red-50 border border-red-200 text-red-600"
                    }`}
                >
                  <AlertCircle size={18} className="mx-auto mb-2" />
                  {error}
                </div>
              )}
              <div className="group">
                <label
                  className={`block mb-2 text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                    }`}
                >
                  Meeting Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={createMeetingForm.title}
                    onChange={(e) => {
                      const newTitle = e.target.value;
                      setCreateMeetingForm({ ...createMeetingForm, title: newTitle });

                      if (meetingId) {
                        const baseUrl = process.env.REACT_APP_MEETING_BASE_URL || "https://dev-meeting.vela.business/";

                        // YAHAN BHI SLUGIFY USE KARO:
                        const slugified = slugifyTitle(newTitle);
                        const safeTitle = slugified ? `-${slugified}` : "";

                        setLink(`${baseUrl}${meetingId}${safeTitle}`);
                      }

                      if (previewTimeoutRef.current) {
                        clearTimeout(previewTimeoutRef.current);
                      }

                      if (!newTitle.trim()) {
                        setMeetingId("");
                        setLink("");
                        return;
                      }

                      previewTimeoutRef.current = setTimeout(() => {
                        previewRoomId();
                      }, 600);
                    }}


                    placeholder="Enter meeting name"
                    className={`w-full rounded-xl px-4 py-3 transition-all duration-200 text-base focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 ${darkMode
                      ? "bg-white/5 border border-white/10 text-white placeholder-white/40"
                      : "bg-white border border-gray-200 text-gray-900 placeholder-gray-500"
                      }`}
                  />
                </div>
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Date */}
                <div className="group">
                  <label
                    className={`block mb-2 text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                      }`}
                  >
                    Date
                  </label>
                  <div className="relative">
                    <DatePicker
                      selected={date}
                      onChange={setDate}
                      minDate={new Date()}
                      dateFormat="MMMM d, yyyy"
                      className={`w-full rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all duration-200 pr-12 text-base ${darkMode
                        ? "bg-white/5 border border-white/10 text-white"
                        : "bg-white border border-gray-200 text-gray-900"
                        }`}
                      calendarClassName="custom-datepicker-calendar"
                      popperClassName="custom-datepicker-popper"
                    />
                    <CalendarIcon
                      className={`absolute right-4 top-1/2 -translate-y-1/2 ${darkMode ? "text-white/40" : "text-gray-400"
                        }`}
                      size={18}
                    />
                  </div>
                </div>

                {/* Start Time - with typing support */}
                <div className="group" ref={startTimeRef}>
                  <label
                    className={`block mb-2 text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                      }`}
                  >
                    Start Time
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={startTimeInput || startTime}
                      onChange={(e) => handleStartTimeInputChange(e.target.value)}
                      onFocus={() => setShowStartTimeDropdown(true)}
                      onBlur={handleStartTimeInputBlur}
                      placeholder="Type or select time"
                      className={`w-full rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all duration-200 text-base cursor-text ${darkMode
                        ? `bg-white/5 border ${showError ? "border-red-500" : "border-white/10"
                        } text-white placeholder-white/40`
                        : `bg-white border ${showError ? "border-red-500" : "border-gray-200"
                        } text-gray-900 placeholder-gray-500`
                        }`}
                    />
                    <Clock
                      className={`absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none ${darkMode ? "text-white/40" : "text-gray-400"
                        }`}
                      size={18}
                    />
                    {showError && (
                      <AlertCircle
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-red-500"
                        size={16}
                      />
                    )}

                    {/* Time Dropdown */}
                    {showStartTimeDropdown && (
                      <div
                        className={`absolute top-full left-0 right-0 mt-2 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto ${darkMode
                          ? "bg-[#1a1a2e] border border-white/20"
                          : "bg-white border border-gray-200"
                          }`}
                      >
                        {getFilteredStartTimes().map((t) => (
                          <button
                            key={t}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setStartTime(t);
                              setStartTimeInput('');
                              setShowStartTimeDropdown(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm transition-colors ${t === startTime
                              ? darkMode
                                ? "bg-pink-500/20 text-pink-400"
                                : "bg-pink-50 text-pink-600"
                              : darkMode
                                ? "text-white hover:bg-white/10"
                                : "text-gray-900 hover:bg-gray-50"
                              }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* End Time - with typing support */}
                <div className="group" ref={endTimeRef}>
                  <label
                    className={`block mb-2 text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                      }`}
                  >
                    End Time
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={endTimeInput || endTime}
                      onChange={(e) => handleEndTimeInputChange(e.target.value)}
                      onFocus={() => setShowEndTimeDropdown(true)}
                      onBlur={handleEndTimeInputBlur}
                      placeholder="Type or select time"
                      className={`w-full rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all duration-200 text-base cursor-text ${darkMode
                        ? `bg-white/5 border ${showError ? "border-red-500" : "border-white/10"
                        } text-white placeholder-white/40`
                        : `bg-white border ${showError ? "border-red-500" : "border-gray-200"
                        } text-gray-900 placeholder-gray-500`
                        }`}
                    />
                    <Clock
                      className={`absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none ${darkMode ? "text-white/40" : "text-gray-400"
                        }`}
                      size={18}
                    />
                    {showError && (
                      <AlertCircle
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-red-500"
                        size={16}
                      />
                    )}

                    {/* Time Dropdown */}
                    {showEndTimeDropdown && (
                      <div
                        className={`absolute top-full left-0 right-0 mt-2 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto ${darkMode
                          ? "bg-[#1a1a2e] border border-white/20"
                          : "bg-white border border-gray-200"
                          }`}
                      >
                        {getFilteredEndTimes().map((t) => (
                          <button
                            key={t}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setEndTime(t);
                              setEndTimeInput('');
                              setShowEndTimeDropdown(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm transition-colors ${t === endTime
                              ? darkMode
                                ? "bg-pink-500/20 text-pink-400"
                                : "bg-pink-50 text-pink-600"
                              : darkMode
                                ? "text-white hover:bg-white/10"
                                : "text-gray-900 hover:bg-gray-50"
                              }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Recurring */}
              <div className="group">
                <label
                  className={`block mb-3 text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                    }`}
                >
                  Recurring Meeting
                </label>
                <div
                  className={`rounded-xl p-4 border ${darkMode
                    ? "bg-white/5 border-white/10"
                    : "bg-gray-50 border-gray-200"
                    }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={recurring}
                          onChange={(e) => setRecurring(e.target.checked)}
                          className={`w-4 h-4 text-pink-600 rounded focus:ring-pink-500 focus:ring-2 transition-all duration-200 ${darkMode
                            ? "bg-white/10 border-white/20"
                            : "bg-white border-gray-300"
                            }`}
                        />
                        <div
                          className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded pointer-events-none ${darkMode
                            ? "bg-gradient-to-r from-pink-600/20 to-red-500/20"
                            : "bg-gradient-to-r from-pink-600/10 to-red-500/10"
                            }`}
                        ></div>
                      </div>
                      <div>
                        <span
                          className={`font-medium text-sm ${darkMode ? "text-white" : "text-gray-900"
                            }`}
                        >
                          Enable recurring meeting
                        </span>
                        <p
                          className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                            }`}
                        >
                          Set up a recurring meeting schedule
                        </p>
                      </div>
                    </label>

                    {recurring && (
                      <div className="flex flex-col gap-4 sm:ml-auto">
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-sm ${darkMode ? "text-white/70" : "text-gray-700"
                              }`}
                          >
                            Repeat
                          </span>
                          <select
                            value={recurringFrequency}
                            onChange={(e) =>
                              setRecurringFrequency(e.target.value as "daily" | "weekly" | "biweekly" | "monthly" | "yearly")
                            }
                            className={`rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all duration-200 ${darkMode
                              ? "bg-white/10 border border-white/20 text-white"
                              : "bg-white border border-gray-200 text-gray-900"
                              }`}
                          >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="biweekly">Bi-weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="yearly">Yearly</option>
                          </select>
                        </div>

                        {/* Recurring Date Range */}
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label
                              className={`block mb-2 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                                }`}
                            >
                              Start Date
                            </label>
                            <DatePicker
                              selected={recurringStartDate}
                              onChange={setRecurringStartDate}
                              minDate={new Date()}
                              dateFormat="MMM d, yyyy"
                              className={`w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 ${darkMode
                                ? "bg-white/10 border border-white/20 text-white"
                                : "bg-white border border-gray-200 text-gray-900"
                                }`}
                            />
                          </div>
                          <div>
                            <label
                              className={`block mb-2 text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                                }`}
                            >
                              End Date (Optional)
                            </label>
                            <DatePicker
                              selected={recurringEndDate}
                              onChange={setRecurringEndDate}
                              minDate={recurringStartDate || new Date()}
                              dateFormat="MMM d, yyyy"
                              placeholderText="No end date"
                              isClearable
                              className={`w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 ${darkMode
                                ? "bg-white/10 border border-white/20 text-white"
                                : "bg-white border border-gray-200 text-gray-900"
                                }`}
                            />
                          </div>
                        </div>

                        {recurringFrequency !== "daily" && (
                          <div className="flex flex-col gap-3">
                            <span
                              className={`text-sm ${darkMode ? "text-white/70" : "text-gray-700"
                                }`}
                            >
                              On days:
                            </span>
                            <div className="flex flex-wrap gap-2">
                              {[
                                { value: "sunday", label: "S" },
                                { value: "monday", label: "M" },
                                { value: "tuesday", label: "T" },
                                { value: "wednesday", label: "W" },
                                { value: "thursday", label: "TH" },
                                { value: "friday", label: "F" },
                                { value: "saturday", label: "SA" },
                              ].map((day) => (
                                <button
                                  key={day.value}
                                  type="button"
                                  onClick={() => handleDayToggle(day.value)}
                                  className={`w-10 h-10 rounded-lg border-2 transition-all duration-200 flex items-center justify-center text-sm font-medium ${recurringDays.includes(day.value)
                                    ? "bg-gradient-to-r from-pink-600 to-red-500 border-pink-500 text-white shadow-lg"
                                    : darkMode
                                      ? "bg-white/5 border-white/20 text-white/60 hover:bg-white/10 hover:border-white/30 hover:text-white"
                                      : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800"
                                    }`}
                                >
                                  {day.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="group">
                <div className="flex items-center justify-between mb-3">
                  <label
                    className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                      }`}
                  >
                    Description (Optional)
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowDescription(!showDescription)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200 ${showDescription
                      ? darkMode
                        ? "bg-red-600/20 text-red-400 hover:bg-red-600/30"
                        : "bg-red-50 text-red-600 hover:bg-red-100"
                      : darkMode
                        ? "bg-white/10 text-white/60 hover:bg-white/20 hover:text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
                      }`}
                  >
                    {showDescription ? <X size={16} /> : <Plus size={16} />}
                  </button>
                </div>
                {showDescription && (
                  <div className="relative">
                    <textarea
                      value={createMeetingForm.description}
                      onChange={(e) => setCreateMeetingForm({
                        ...createMeetingForm,
                        description: e.target.value
                      })}
                      placeholder="Enter meeting description and agenda..."
                      className={`w-full rounded-xl px-4 py-3 transition-all duration-200 min-h-[100px] resize-none text-base focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 ${darkMode
                        ? "bg-white/5 border border-white/10 text-white placeholder-white/40"
                        : "bg-white border border-gray-200 text-gray-900 placeholder-gray-500"
                        }`}
                    />
                  </div>
                )}
              </div>

              {/* Participants */}
              <div className="group participant-input-container">
                <label
                  className={`block mb-3 text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                    }`}
                >
                  Participants
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={participantInput}
                    onChange={(e) =>
                      handleParticipantInputChange(e.target.value)
                    }
                    onKeyPress={handleKeyPress}
                    onFocus={handleParticipantInputFocus}
                    placeholder="Search by name or email..."
                    className={`w-full rounded-xl px-4 py-3 transition-all duration-200 pr-12 text-base focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 ${darkMode
                      ? "bg-white/5 border border-white/10 text-white placeholder-white/40"
                      : "bg-white border border-gray-200 text-gray-900 placeholder-gray-500"
                      }`}
                  />
                  <Users
                    className={`absolute right-4 top-1/2 -translate-y-1/2 ${darkMode ? "text-white/40" : "text-gray-400"
                      }`}
                    size={18}
                  />

                  {/* Autocomplete Suggestions */}
                  {showParticipantSuggestions && (
                    <div
                      className={`absolute top-full left-0 right-0 mt-2 rounded-xl shadow-lg z-50 max-h-48 overflow-y-auto backdrop-blur-sm ${darkMode
                        ? "bg-white/10 border border-white/20"
                        : "bg-white border border-gray-200"
                        }`}
                    >
                      {/* Loading State */}
                      {loading && (
                        <div className={`p-3 text-center ${darkMode ? "text-white/60" : "text-gray-500"
                          }`}>
                          <div className="flex items-center justify-center">
                            <RefreshCw size={16} className="animate-spin mr-2" />
                            Searching users...
                          </div>
                        </div>
                      )}

                      {!loading && filteredSuggestions.length > 0 ? (
                        filteredSuggestions.map((user) => (
                          <button
                            key={user.id}
                            type="button"
                            onClick={() => handleAddParticipant(user.email)}
                            className={`w-full flex items-center gap-3 p-3 transition-colors duration-200 text-left ${darkMode
                              ? "hover:bg-white/10"
                              : "hover:bg-gray-50"
                              }`}
                          >
                            {user.avatar ? (
                              <img
                                src={`${backendURL}${user.avatar}`}
                                alt={user.name}
                                className="w-8 h-8 rounded-full border border-white/20"
                              />
                            ) : (
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${darkMode ? "bg-white/20 text-white" : "bg-gray-200 text-gray-700"
                                }`}>
                                {user.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div
                                className={`font-medium text-sm truncate ${darkMode ? "text-white" : "text-gray-900"
                                  }`}
                              >
                                {user.name}
                                {user.isMockUser === false && (
                                  <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${darkMode ? "bg-blue-900/50 text-blue-200" : "bg-blue-100 text-blue-800"
                                    }`}>
                                    User
                                  </span>
                                )}
                              </div>
                              <div
                                className={`text-xs truncate ${darkMode ? "text-white/60" : "text-gray-600"
                                  }`}
                              >
                                {user.email}
                              </div>
                            </div>
                          </button>
                        ))
                      ) : (
                        !loading && (
                          <div
                            className={`p-3 text-center ${darkMode ? "text-white/60" : "text-gray-500"
                              }`}
                          >
                            No matching users found
                            {isValidEmail(participantInput) ? (
                              <div className="mt-2">
                                <button
                                  className={`text-sm px-3 py-1.5 rounded-lg ${darkMode
                                    ? "bg-blue-600/30 hover:bg-blue-600/50 text-white"
                                    : "bg-blue-50 hover:bg-blue-100 text-blue-600"
                                    }`}
                                  onClick={() => handleAddParticipant(participantInput.trim())}
                                >
                                  Add {participantInput.trim()} as external guest
                                </button>
                              </div>
                            ) : (
                              participantInput.includes('@') && (
                                <div className="mt-2 text-xs text-red-500">
                                  Enter a valid email address
                                </div>
                              )
                            )}
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>

                <p
                  className={`text-xs mt-2 ${darkMode ? "text-white/40" : "text-gray-500"
                    }`}
                >
                  Type to search or press Enter to add
                </p>

                {duplicateMsg && (
                  <p className="mt-1 text-xs text-amber-500 font-medium">{duplicateMsg}</p>
                )}

                {/* Selected Participants */}
                {participantEmails.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <div
                      className={`text-xs font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                        }`}
                    >
                      Selected participants:
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {participantEmails.map((email) => {
                        const user = mockUsers.find((u) => u.email === email);
                        return (
                          <div
                            key={email}
                            className={`flex items-center gap-2 rounded-lg px-3 py-2 ${darkMode
                              ? "bg-white/10 border border-white/20"
                              : "bg-gray-100 border border-gray-200"
                              }`}
                          >
                            {user && (
                              <img
                                src={user.avatar}
                                alt={user.name}
                                className="w-6 h-6 rounded-full border border-white/20"
                              />
                            )}
                            <span
                              className={`text-sm ${darkMode ? "text-white" : "text-gray-900"
                                }`}
                            >
                              {user ? user.name : email}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveParticipant(email)}
                              className={`transition-colors duration-200 ${darkMode
                                ? "text-white/40 hover:text-white/80"
                                : "text-gray-400 hover:text-gray-600"
                                }`}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Meeting Link */}
              <div className="group">
                <label
                  className={`block mb-3 text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-700"
                    }`}
                >
                  Meeting Link
                </label>
                <div
                  className={`rounded-xl p-4 border ${darkMode
                    ? "bg-white/5 border-white/10"
                    : "bg-gray-50 border-gray-200"
                    }`}
                >
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="text"
                      value={link}
                      readOnly
                      className={`flex-1 rounded-lg px-3 py-2 text-sm ${darkMode
                        ? "bg-white/10 border border-white/20 text-white/80"
                        : "bg-white border border-gray-200 text-gray-600"
                        }`}
                    />
                    <button
                      type="button"
                      onClick={handleCopy}
                      className={`px-4 py-2 rounded-lg transition-all duration-200 flex items-center justify-center ${copied ? "bg-green-500 text-white" : ""} gap-2 text-sm whitespace-nowrap ${darkMode
                        ? `bg-white/10 border border-white/20 text-white ${copied ? "hover:bg-green-500" : "hover:bg-white/20"}`
                        : `bg-gray-100 border border-gray-200 text-gray-700 ${copied ? "hover:bg-green-500" : "hover:bg-gray-200"}`
                        }`}
                    >
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                      {copied ? "Copied" : "Copy Link"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "security" && (
            <div className="space-y-5">
              {/* Security Settings */}
              <div
                className={`rounded-xl p-4 border ${darkMode
                  ? "bg-white/5 border-white/10"
                  : "bg-gray-50 border-gray-200"
                  }`}
              >
                <div className="space-y-4">
                  {/* Password Protection */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h4
                        className={`font-medium text-sm ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        Password Protection
                      </h4>
                      <p
                        className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                          }`}
                      >
                        Require a password to join the meeting
                      </p>
                    </div>
                    <button
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.requirePassword
                        ? "bg-gradient-to-r from-pink-500 to-red-500"
                        : darkMode
                          ? "bg-white/20"
                          : "bg-gray-300"
                        }`}
                      onClick={() => {
                        const newRequirePassword =
                          !securitySettings.requirePassword;
                        setSecuritySettings({
                          ...securitySettings,
                          requirePassword: newRequirePassword,
                          password: "",
                        });
                        if (newRequirePassword) {
                          setPasswordConfirmed(false);
                        }
                      }}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full transition-transform ${securitySettings.requirePassword
                          ? "translate-x-6"
                          : "translate-x-1"
                          } ${darkMode ? "bg-white" : "bg-white shadow-sm"}`}
                      />
                    </button>
                  </div>

                  {securitySettings.requirePassword && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <label
                          className={`text-sm font-medium whitespace-nowrap ${darkMode ? "text-white/70" : "text-gray-700"
                            }`}
                        >
                          Meeting Password
                        </label>
                        <div className="w-64 relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter Your Password"
                            className={`w-full rounded-lg px-3 py-2 pr-20 transition-all duration-200 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 ${darkMode
                              ? "bg-white/10 border border-white/20 text-white placeholder-white/40"
                              : "bg-white border border-gray-200 text-gray-900 placeholder-gray-500"
                              }`}
                            value={securitySettings.password}
                            // readOnly
                            onChange={(e) => setSecuritySettings({
                              ...securitySettings,
                              password: e.target.value
                            })}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${darkMode
                              ? "text-white/60 hover:text-white"
                              : "text-gray-400 hover:text-gray-600"
                              }`}
                          >
                            {showPassword ? (
                              <Eye size={16} />
                            ) : (
                              <EyeOff size={16} />
                            )}
                          </button>
                        </div>
                        {/* Copy button */}
                        <CopyButton
                          text={securitySettings.password}
                          darkMode={darkMode}
                        />
                      </div>
                    </div>
                  )}

                  {/* Waiting Room */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h4
                        className={`font-medium text-sm ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        Waiting Room
                      </h4>
                      <p
                        className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                          }`}
                      >
                        Participants must be admitted by the host
                      </p>
                    </div>
                    <button
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.waitingRoom
                        ? "bg-gradient-to-r from-pink-500 to-red-500"
                        : darkMode
                          ? "bg-white/20"
                          : "bg-gray-300"
                        }`}
                      onClick={() =>
                        setSecuritySettings({
                          ...securitySettings,
                          waitingRoom: !securitySettings.waitingRoom,
                        })
                      }
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full transition-transform ${securitySettings.waitingRoom
                          ? "translate-x-6"
                          : "translate-x-1"
                          } ${darkMode ? "bg-white" : "bg-white shadow-sm"}`}
                      />
                    </button>
                  </div>

                  {/* Mute on Entry */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h4
                        className={`font-medium text-sm ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        Mute on Entry
                      </h4>
                      <p
                        className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                          }`}
                      >
                        Automatically mute participants when they join
                      </p>
                    </div>
                    <button
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.muteOnEntry
                        ? "bg-gradient-to-r from-pink-500 to-red-500"
                        : darkMode
                          ? "bg-white/20"
                          : "bg-gray-300"
                        }`}
                      onClick={() =>
                        setSecuritySettings({
                          ...securitySettings,
                          muteOnEntry: !securitySettings.muteOnEntry,
                        })
                      }
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full transition-transform ${securitySettings.muteOnEntry
                          ? "translate-x-6"
                          : "translate-x-1"
                          } ${darkMode ? "bg-white" : "bg-white shadow-sm"}`}
                      />
                    </button>
                  </div>

                  {/* Allow Recording */}
                  {/* <div className="flex items-center justify-between">
                    <div>
                      <h4
                        className={`font-medium text-sm ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        Allow Recording
                      </h4>
                      <p
                        className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                          }`}
                      >
                        Participants can record the meeting
                      </p>
                    </div>
                    <button
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${securitySettings.recording
                        ? "bg-gradient-to-r from-pink-500 to-red-500"
                        : darkMode
                          ? "bg-white/20"
                          : "bg-gray-300"
                        }`}
                      onClick={() =>
                        setSecuritySettings({
                          ...securitySettings,
                          recording: !securitySettings.recording,
                        })
                      }
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full transition-transform ${securitySettings.recording
                          ? "translate-x-6"
                          : "translate-x-1"
                          } ${darkMode ? "bg-white" : "bg-white shadow-sm"}`}
                      />
                    </button>
                  </div> */}

                  {/* Assign Moderators */}
                  <div
                    className={`pt-4 border-t ${darkMode ? "border-white/10" : "border-gray-200"
                      }`}
                  >
                    <div className="mb-3">
                      <h4
                        className={`font-medium text-sm mb-1 ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        Assign Moderators
                      </h4>
                      <p
                        className={`text-xs ${darkMode ? "text-white/60" : "text-gray-600"
                          }`}
                      >
                        Select participants to have moderator privileges (you
                        are automatically a moderator)
                      </p>
                    </div>

                    {participantEmails.length > 0 ? (
                      <div className="space-y-2">
                        {participantEmails.map((email) => {
                          const user = mockUsers.find((u) => u.email === email);
                          return (
                            <label
                              key={email}
                              className="flex items-center gap-3 cursor-pointer group"
                            >
                              <div className="relative">
                                <input
                                  type="checkbox"
                                  checked={securitySettings.moderators.includes(
                                    email
                                  )}
                                  onChange={() => handleModeratorToggle(email)}
                                  className={`w-4 h-4 text-pink-600 rounded focus:ring-pink-500 focus:ring-2 transition-all duration-200 ${darkMode
                                    ? "bg-white/10 border-white/20"
                                    : "bg-white border-gray-300"
                                    }`}
                                />
                                <div
                                  className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded pointer-events-none ${darkMode
                                    ? "bg-gradient-to-r from-pink-600/20 to-red-500/20"
                                    : "bg-gradient-to-r from-pink-600/10 to-red-500/10"
                                    }`}
                                ></div>
                              </div>
                              <div className="flex items-center gap-2">
                                {user ? (
                                  <img
                                    src={user.avatar}
                                    alt={user.name}
                                    className={`w-6 h-6 rounded-full border ${darkMode
                                      ? "border-white/20"
                                      : "border-gray-200"
                                      }`}
                                  />
                                ) : (
                                  <div
                                    className={`w-6 h-6 rounded-full flex items-center justify-center ${darkMode
                                      ? "bg-white/10 border border-white/20"
                                      : "bg-gray-100 border border-gray-200"
                                      }`}
                                  >
                                    <span
                                      className={`text-xs ${darkMode
                                        ? "text-white/60"
                                        : "text-gray-500"
                                        }`}
                                    >
                                      ?
                                    </span>
                                  </div>
                                )}
                                <span
                                  className={`text-sm ${darkMode ? "text-white" : "text-gray-900"
                                    }`}
                                >
                                  {user ? user.name : email}
                                </span>
                                {securitySettings.moderators.includes(
                                  email
                                ) && (
                                    <span className="px-2 py-1 bg-gradient-to-r from-pink-500 to-red-500 text-white text-xs rounded-full font-medium">
                                      Moderator
                                    </span>
                                  )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <div
                          className={`w-12 h-12 mx-auto mb-2 flex items-center justify-center rounded-full ${darkMode ? "bg-white/5" : "bg-gray-100"
                            }`}
                        >
                          <Users
                            size={20}
                            className={
                              darkMode ? "text-white/40" : "text-gray-400"
                            }
                          />
                        </div>
                        <p
                          className={`text-xs ${darkMode ? "text-white/40" : "text-gray-500"
                            }`}
                        >
                          No participants added yet
                        </p>
                        <p
                          className={`text-xs mt-1 ${darkMode ? "text-white/30" : "text-gray-400"
                            }`}
                        >
                          Add participants in the Details tab to assign
                          moderators
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div
          className={`flex gap-4 p-6 pt-4 pb-6 border-t ${darkMode ? "border-white/10" : "border-gray-200"
            }`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`px-8 py-3.5 rounded-xl transition-all duration-200 text-sm font-medium ${darkMode
              ? "border border-white/20 text-white hover:bg-white/10"
              : "border border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleScheduleMeeting}
            className="flex-1 bg-gradient-to-r from-pink-500 to-red-500 text-white py-3.5 rounded-xl font-semibold hover:scale-105 transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            disabled={loading || !createMeetingForm.title.trim()}
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Creating Meeting...
              </div>
            ) : (
              "Create Meeting"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateMeeting;
